/**
 * Lengths as a producer types and reads them. Everything is stored in feet;
 * these turn a typed length — `6.5`, `6'`, `6' 6"`, `6ft 6in`, `78"`,
 * `6' 6 1/2"` — into feet, and feet back into the words on screen.
 */
export const LENGTH_UNITS = ['ft', 'ftin'];
export function isLengthUnits(value) {
    return value === 'ft' || value === 'ftin';
}
const FEET = `(?:'|ft\\.?|feet|foot)`;
const INCHES = `(?:"|''|in\\.?|inch|inches)`;
const NUMBER = `(\\d+(?:\\.\\d*)?|\\.\\d+)`;
// Whole inches with an optional fraction ("6 1/2"), or a fraction alone ("1/2").
const INCH_AMOUNT = `(?:${NUMBER}(?:\\s+(\\d+)\\/(\\d+))?|(\\d+)\\/(\\d+))`;
const FEET_ONLY = new RegExp(`^${NUMBER}\\s*${FEET}?$`);
const INCHES_ONLY = new RegExp(`^${INCH_AMOUNT}\\s*${INCHES}$`);
const FEET_AND_INCHES = new RegExp(`^${NUMBER}\\s*${FEET}[\\s-]*${INCH_AMOUNT}\\s*${INCHES}?$`);
function inchesFrom(whole, num, den, fracNum, fracDen) {
    const fraction = (n, d) => {
        if (n === undefined || d === undefined)
            return 0;
        const denominator = Number(d);
        return denominator > 0 ? Number(n) / denominator : NaN;
    };
    const value = whole !== undefined ? Number(whole) + fraction(num, den) : fraction(fracNum, fracDen);
    return Number.isFinite(value) ? value : null;
}
/**
 * Feet from a typed length, or `null` when it is not one. A bare number is
 * feet, as the fields have always read it; a trailing number after feet is
 * inches (`6' 6`); curly quotes and primes count as feet and inch marks.
 */
export function parseLength(text) {
    const input = text
        .trim()
        .toLowerCase()
        .replace(/[\u2018\u2019\u2032]/g, "'")
        .replace(/[\u201c\u201d\u2033]/g, '"')
        .replace(/\s+/g, ' ');
    if (!input)
        return null;
    return finite(readLength(input));
}
/** A pasted run of digits can overflow to Infinity, which is no length. */
function finite(feet) {
    return feet !== null && Number.isFinite(feet) ? feet : null;
}
function readLength(input) {
    let m = FEET_ONLY.exec(input);
    if (m)
        return Number(m[1]);
    m = INCHES_ONLY.exec(input);
    if (m) {
        const inches = inchesFrom(m[1], m[2], m[3], m[4], m[5]);
        return inches === null ? null : inches / 12;
    }
    m = FEET_AND_INCHES.exec(input);
    if (m) {
        const inches = inchesFrom(m[2], m[3], m[4], m[5], m[6]);
        return inches === null ? null : Number(m[1]) + inches / 12;
    }
    return null;
}
/** Feet and inches, the inches to the nearest half; 12" rolls into the next foot. */
function feetAndInches(feet) {
    const sign = feet < 0 ? '-' : '';
    const halfInches = Math.round(Math.abs(feet) * 24);
    return { sign, ft: Math.floor(halfInches / 24), inches: (halfInches % 24) / 2 };
}
function trimNumber(n) {
    return String(Math.round(n * 10) / 10);
}
/**
 * A length for an input field: `6.5` in feet (a bare number, as the field has
 * always shown), `6' 6"` in feet and inches.
 */
export function formatLengthInput(feet, units) {
    if (!Number.isFinite(feet))
        return '';
    if (units === 'ft')
        return trimNumber(feet);
    const { sign, ft, inches } = feetAndInches(feet);
    if (ft === 0 && inches === 0)
        return `0'`;
    if (ft === 0)
        return `${sign}${inches}"`;
    if (inches === 0)
        return `${sign}${ft}'`;
    return `${sign}${ft}' ${inches}"`;
}
/** A length to read: `6.5'` in feet, `6' 6"` in feet and inches. */
export function formatLength(feet, units) {
    if (!Number.isFinite(feet))
        return '';
    return units === 'ft' ? `${trimNumber(feet)}'` : formatLengthInput(feet, 'ftin');
}
//# sourceMappingURL=lengths.js.map